// layoutPageUtils.js

// Utilities
import {
  normalizeRichTextValue,
  serializeRichTextDocument,
} from 'utilities/RichText';

// Components
import { normalizePageNodePresentation } from 'page/LayoutNodePresentation';

/**
 * normalizeString
 *
 * Returns a trimmed string when the input is a string and an empty string otherwise.
 *
 * @param {unknown} value - Value to normalize.
 * @returns {string} The normalized string value.
 */
function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * buildPageDraft
 *
 * Returns the editable page draft values for the active layout and page.
 *
 * @param {object} options - Draft source values.
 * @param {object|null} options.layout - Active layout document.
 * @param {object|null} options.page - Active node-specific page document.
 * @returns {{path: string, pin: string}} The normalized page draft state.
 */
function buildPageDraft({ page }) {
  return {
    pin: normalizeString(page?.pin),
  };
}

/**
 * buildRichTextOverrideMap
 *
 * Returns a normalized rich text override map keyed by block key.
 *
 * @param {unknown} overrides - Candidate rich text overrides.
 * @returns {object} The normalized rich text overrides keyed by block key.
 */
function buildRichTextOverrideMap(overrides) {
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
    return {};
  }

  return Object.entries(overrides).reduce((next, [blockKey, value]) => {
    const cleanedBlockKey = normalizeString(blockKey);
    if (!cleanedBlockKey) {
      return next;
    }

    next[cleanedBlockKey] = normalizeRichTextValue(value);
    return next;
  }, {});
}

/**
 * buildPageBlockOverrideMap
 *
 * Returns a sanitized page block override map keyed by block key.
 *
 * @param {unknown} overrides - Candidate page block overrides.
 * @returns {object} The sanitized block override map.
 */
function buildPageBlockOverrideMap(overrides) {
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
    return {};
  }

  return Object.entries(overrides).reduce((next, [blockKey, value]) => {
    const cleanedBlockKey = normalizeString(blockKey);
    if (!cleanedBlockKey || !value || typeof value !== 'object' || Array.isArray(value)) {
      return next;
    }

    const sanitizedValue = {
      ...(Object.prototype.hasOwnProperty.call(value, 'titleOverride')
        ? { titleOverride: normalizeString(value?.titleOverride) || null }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(value, 'rollupKey')
        ? { rollupKey: normalizeString(value?.rollupKey) || null }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(value, 'typeKey')
        ? { typeKey: normalizeString(value?.typeKey) || null }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(value, 'nodeKey')
        ? { nodeKey: normalizeString(value?.nodeKey) || null }
        : {}),
      ...(Object.prototype.hasOwnProperty.call(value, 'presentation')
        ? { presentation: normalizePageNodePresentation(value?.presentation) }
        : {}),
    };

    if (!Object.keys(sanitizedValue).length) {
      return next;
    }

    next[cleanedBlockKey] = sanitizedValue;
    return next;
  }, {});
}

/**
 * isPageOverrideBlockType
 *
 * Returns whether the provided block supports node-scoped page overrides.
 *
 * @param {object|null} block - Candidate layout block.
 * @returns {boolean} True when the block is a rollup or node block.
 */
function isPageOverrideBlockType(block) {
  const blockType = normalizeString(block?.type).toUpperCase();
  return blockType === 'ROLLUP' || blockType === 'NODE';
}

/**
 * buildPageBlockOverrideValue
 *
 * Returns the persisted override payload for a page-aware block.
 *
 * @param {object|null} block - Candidate block configuration.
 * @returns {object|null} The persisted override payload or null when the block is incomplete.
 */
function buildPageBlockOverrideValue(block) {
  const blockType = normalizeString(block?.type).toUpperCase();

  if (blockType === 'ROLLUP' && normalizeString(block?.rollupKey) && normalizeString(block?.typeKey)) {
    return {
      rollupKey: normalizeString(block?.rollupKey) || null,
      titleOverride: normalizeString(block?.titleOverride) || null,
      typeKey: normalizeString(block?.typeKey) || null,
    };
  }

  if (blockType === 'NODE' && normalizeString(block?.nodeKey)) {
    return {
      nodeKey: normalizeString(block?.nodeKey) || null,
      presentation: normalizePageNodePresentation(block?.presentation),
      titleOverride: normalizeString(block?.titleOverride) || null,
    };
  }

  return null;
}

/**
 * serializeRichTextValue
 *
 * Serializes a rich text value after normalizing its document shape.
 *
 * @param {unknown} value - Rich text value to serialize.
 * @returns {string} The serialized rich text document.
 */
function serializeRichTextValue(value) {
  return serializeRichTextDocument(normalizeRichTextValue(value).content);
}

/**
 * isRichTextValueEmpty
 *
 * Returns whether a rich text value is empty after normalization.
 *
 * @param {unknown} value - Candidate rich text value.
 * @returns {boolean} Whether the value is empty.
 */
function isRichTextValueEmpty(value) {
  return serializeRichTextValue(value) === serializeRichTextValue(null);
}

/**
 * buildLayoutEditorDocument
 *
 * Returns the shared layout editor document for the active layout.
 *
 * @param {object|null} layoutDoc - Active layout document.
 * @param {object|null} [pageTextOverridesByBlockKey] - Legacy page text overrides.
 * @returns {object|null} The editor document payload.
 */
function buildLayoutEditorDocument(layoutDoc, pageTextOverridesByBlockKey = null) {
  if (!layoutDoc) {
    return null;
  }

  const baseBlocksByKey = layoutDoc.blocksByKey && typeof layoutDoc.blocksByKey === 'object'
    ? layoutDoc.blocksByKey
    : {};
  const legacyTextOverrides = pageTextOverridesByBlockKey
    && typeof pageTextOverridesByBlockKey === 'object'
    && !Array.isArray(pageTextOverridesByBlockKey)
    ? pageTextOverridesByBlockKey
    : null;
  const blocksByKey = legacyTextOverrides
    ? Object.entries(baseBlocksByKey).reduce((next, [blockKey, block]) => {
      const cleanedBlockKey = normalizeString(blockKey);
      if (!cleanedBlockKey) {
        return next;
      }

      if (
        normalizeString(block?.type).toUpperCase() !== 'RICH_TEXT'
        || !isRichTextValueEmpty(block?.value)
        || !legacyTextOverrides[cleanedBlockKey]
      ) {
        next[cleanedBlockKey] = block;
        return next;
      }

      next[cleanedBlockKey] = {
        ...block,
        value: normalizeRichTextValue(legacyTextOverrides[cleanedBlockKey]),
      };
      return next;
    }, {})
    : baseBlocksByKey;

  return {
    accessMode: 'PUBLIC',
    blocksByKey,
    key: layoutDoc.key,
    layout: layoutDoc.layout || null,
    status: 'active',
    title: layoutDoc.title || 'Untitled Page',
    baseKey: layoutDoc.baseKey || null,
  };
}

/**
 * buildLayoutMetadataItems
 *
 * Returns metadata rows for the shared layout editor surface.
 *
 * @param {object|null} layoutDoc - Active layout document.
 * @returns {object[]} The metadata rows.
 */
function buildLayoutMetadataItems(layoutDoc) {
  if (!layoutDoc) {
    return [];
  }

  return [
    {
      label: 'Mode',
      value: 'Layout',
    },
    {
      label: 'Type',
      value: layoutDoc.typeKey || 'Unknown',
    },
  ];
}

/**
 * buildViewBlockAllowedTypeKeys
 *
 * Returns unique related type keys allowed for layout view blocks.
 *
 * @param {object} options - Related node sources.
 * @param {object[]} options.relatedChildNodes - Direct related child nodes.
 * @param {object[]} options.relatedDescendantNodes - Related descendant nodes.
 * @returns {string[]} The unique allowed type keys.
 */
function buildViewBlockAllowedTypeKeys({ relatedChildNodes, relatedDescendantNodes }) {
  return Array.from(new Set(
    [...relatedChildNodes, ...relatedDescendantNodes]
      .map((row) => normalizeString(row?.type))
      .filter(Boolean)
  ));
}

const PORTAL_LAYOUT_ALLOWED_SYSTEM_TYPE_KEYS = new Set([
  'DOCUMENT',
  'FILE',
  'PHOTO',
  'VIDEO',
]);

/**
 * isPageLayoutPickerType
 *
 * Returns whether a type definition should appear in page layout view pickers.
 * Includes all base types plus a small set of media/document system types.
 *
 * @param {object|null} typeDef - Node type definition.
 * @returns {boolean} Whether the type is eligible for page layout view authoring.
 */
function isPageLayoutPickerType(typeDef) {
  const typeKey = normalizeString(typeDef?.key);
  if (!typeKey) {
    return false;
  }

  const isSystemType = typeDef?.system === true || typeDef?.isSystem === true;
  if (!isSystemType) {
    return true;
  }

  return PORTAL_LAYOUT_ALLOWED_SYSTEM_TYPE_KEYS.has(typeKey.toUpperCase());
}

/**
 * buildPageLayoutPickerTypeKeys
 *
 * Returns every type key available in page layout view pickers at design time.
 * Includes all base types plus selected media/document system types, excluding
 * the layout host type.
 *
 * @param {object} options - Picker source values.
 * @param {function|null} options.getAllTypeDefs - Returns all type definitions.
 * @param {string} [options.hostTypeKey=''] - Layout host type key to exclude.
 * @returns {string[]} The unique picker type keys.
 */
function buildPageLayoutPickerTypeKeys({
  getAllTypeDefs = null,
  hostTypeKey = '',
} = {}) {
  if (typeof getAllTypeDefs !== 'function') {
    return [];
  }

  const cleanedHostTypeKey = normalizeString(hostTypeKey);
  const allTypeDefs = Array.isArray(getAllTypeDefs()) ? getAllTypeDefs() : [];

  return allTypeDefs
    .filter(isPageLayoutPickerType)
    .map((typeDef) => normalizeString(typeDef?.key))
    .filter((typeKey) => typeKey && typeKey !== cleanedHostTypeKey);
}

/**
 * buildPageLayoutRelatedTypeKeys
 *
 * Returns related type keys available for page layout view authoring at design time.
 *
 * @param {object} options - Discovery sources.
 * @param {object|null} options.hostTypeDef - Layout host type definition.
 * @param {object[]} [options.relatedChildNodes] - Deprecated; ignored when includeAllBaseTypes is true.
 * @param {object[]} [options.relatedDescendantNodes] - Deprecated; ignored when includeAllBaseTypes is true.
 * @param {function|null} options.getAllTypeDefs - Returns all type definitions.
 * @param {boolean} [options.includeAllBaseTypes=false] - Whether to return the full base picker catalog.
 * @returns {string[]} The unique related type keys.
 */
function buildPageLayoutRelatedTypeKeys({
  hostTypeDef = null,
  relatedChildNodes = [],
  relatedDescendantNodes = [],
  getAllTypeDefs = null,
  includeAllBaseTypes = false,
}) {
  const hostTypeKey = normalizeString(hostTypeDef?.key);

  if (includeAllBaseTypes) {
    return buildPageLayoutPickerTypeKeys({
      getAllTypeDefs,
      hostTypeKey,
    });
  }

  const typeKeys = new Set();

  const suggestedChildren = Array.isArray(hostTypeDef?.suggestedChildren)
    ? hostTypeDef.suggestedChildren
    : (Array.isArray(hostTypeDef?.suggested) ? hostTypeDef.suggested : []);

  suggestedChildren.forEach((entry) => {
    const typeKey = normalizeString(typeof entry === 'string' ? entry : entry?.type || entry?.key);
    if (typeKey) {
      typeKeys.add(typeKey);
    }
  });

  buildViewBlockAllowedTypeKeys({ relatedChildNodes, relatedDescendantNodes }).forEach((typeKey) => {
    typeKeys.add(typeKey);
  });

  if (hostTypeKey) {
    typeKeys.delete(hostTypeKey);
  }

  return Array.from(typeKeys).filter(Boolean);
}

/**
 * getLayoutBlocksByKey
 *
 * Returns the normalized shared layout blocks map.
 *
 * @param {object|null} layoutDoc - Active layout document.
 * @returns {object} The shared layout blocks keyed by block key.
 */
function getLayoutBlocksByKey(layoutDoc) {
  return layoutDoc?.blocksByKey && typeof layoutDoc.blocksByKey === 'object'
    ? layoutDoc.blocksByKey
    : {};
}

/**
 * buildSharedPageTextOverrides
 *
 * Returns text overrides that still correspond to rich text blocks in the next layout.
 *
 * @param {object} options - Override source values.
 * @param {object} options.nextBlocksByKey - Next layout blocks keyed by block key.
 * @param {object} options.pageTextOverridesByBlockKey - Current page text overrides.
 * @returns {object} The filtered text overrides.
 */
function buildSharedPageTextOverrides({ nextBlocksByKey, pageTextOverridesByBlockKey }) {
  return Object.entries(pageTextOverridesByBlockKey || {}).reduce((next, [blockKey, value]) => {
    const cleanedBlockKey = normalizeString(blockKey);
    const matchingBlock = nextBlocksByKey?.[cleanedBlockKey] || null;

    if (normalizeString(matchingBlock?.type).toUpperCase() !== 'RICH_TEXT') {
      return next;
    }

    if (!isRichTextValueEmpty(matchingBlock?.value)) {
      return next;
    }

    next[cleanedBlockKey] = value;
    return next;
  }, {});
}

/**
 * buildSharedPageBlockOverrides
 *
 * Returns block overrides that still correspond to page-aware blocks in the next layout.
 *
 * @param {object} options - Override source values.
 * @param {string} [options.excludedBlockKey] - Optional block key to omit from the result.
 * @param {object} options.nextBlocksByKey - Next layout blocks keyed by block key.
 * @param {object} options.pageBlockOverridesByBlockKey - Current page block overrides.
 * @returns {object} The filtered block overrides.
 */
function buildSharedPageBlockOverrides({
  excludedBlockKey = '',
  nextBlocksByKey,
  pageBlockOverridesByBlockKey,
}) {
  const cleanedExcludedBlockKey = normalizeString(excludedBlockKey);

  return Object.entries(pageBlockOverridesByBlockKey || {}).reduce((next, [blockKey, value]) => {
    const cleanedBlockKey = normalizeString(blockKey);
    const matchingBlock = nextBlocksByKey?.[cleanedBlockKey] || null;

    if (!cleanedBlockKey || cleanedBlockKey === cleanedExcludedBlockKey || !isPageOverrideBlockType(matchingBlock)) {
      return next;
    }

    const nextOverrideValue = buildPageBlockOverrideValue({
      ...matchingBlock,
      ...value,
    });
    if (!nextOverrideValue) {
      return next;
    }

    next[cleanedBlockKey] = nextOverrideValue;
    return next;
  }, {});
}

/**
 * buildNodeBlockSignature
 *
 * Returns a stable signature for comparing shared and page node block values.
 *
 * @param {object|null} block - Candidate node block.
 * @returns {string} The serialized signature.
 */
function buildNodeBlockSignature(block) {
  return JSON.stringify({
    nodeKey: normalizeString(block?.nodeKey) || null,
    presentation: normalizePageNodePresentation(block?.presentation),
    titleOverride: normalizeString(block?.titleOverride) || null,
  });
}

/**
 * buildRollupBlockSignature
 *
 * Returns a stable signature for comparing shared and page rollup block values.
 *
 * @param {object|null} block - Candidate rollup block.
 * @returns {string} The serialized signature.
 */
function buildRollupBlockSignature(block) {
  return JSON.stringify({
    rollupKey: normalizeString(block?.rollupKey) || null,
    titleOverride: normalizeString(block?.titleOverride) || null,
    typeKey: normalizeString(block?.typeKey) || null,
  });
}

export {
  buildLayoutEditorDocument,
  buildLayoutMetadataItems,
  buildNodeBlockSignature,
  buildPageBlockOverrideMap,
  buildPageBlockOverrideValue,
  buildPageDraft,
  buildRichTextOverrideMap,
  buildRollupBlockSignature,
  buildSharedPageBlockOverrides,
  buildSharedPageTextOverrides,
  buildPageLayoutPickerTypeKeys,
  buildPageLayoutRelatedTypeKeys,
  buildViewBlockAllowedTypeKeys,
  isPageLayoutPickerType,
  getLayoutBlocksByKey,
  isRichTextValueEmpty,
  normalizeString,
  serializeRichTextValue,
};